import { useCallback, useEffect, useReducer, useRef, useState, useSyncExternalStore } from "react";
import { api, inboundCallError, userAvatarUrl, type InboundCall } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { Avatar } from "@/components/Avatar";
import { Icon } from "@/components/Icon";
import { useRealtime, type RealtimeEvent } from "@/lib/realtime";
import { hasActiveCall, subscribeActiveCall, useSoftphone } from "@/pages/Chats/softphone";
import { savedAudioDevices } from "@/pages/Chats/useAudioDevices";
import { CallerAvatar } from "./CallerAvatar";
import { setCallStackSlot } from "./callStackSlot";
import { InboundCallModal, type InboundSession } from "./InboundCallModal";
import {
  callerNumber,
  callerTitle,
  elapsedSec,
  fmtClock,
  initialInboundCalls,
  ivrLabel,
  listenersLabel,
  nextExpiry,
  openBlockedReason,
  reduceInboundCalls,
  sessionOnEvent,
  sessionOnSnapshot,
  toastCanOpen,
  type OpenBlock,
  type ToastItem,
} from "./inboundCalls";
import "./inboundCall.css";

/** Сколько раз и как часто переспрашивать список, пока chat-service не сверился с АТС. */
const RESYNC_RETRIES = 15;
const RESYNC_RETRY_MS = 2000;

/** Что сейчас на экране у хоста — для программы «HRDeva» (tray/):
 *  ей решать, показывать ли окно и звонить ли. */
export interface InboundActivity {
  // Звонки, ждущие ответа во всплывашках: в меню, не открыты в окне.
  ringing: string[];
  // Всплывашек на экране — любых, и «взял такой-то», и уезжающих.
  toasts: number;
  // Открыто окно звонка (в том числе свёрнутое).
  session: boolean;
}

function sameActivity(a: InboundActivity, b: InboundActivity): boolean {
  return (
    a.toasts === b.toasts &&
    a.session === b.session &&
    a.ringing.length === b.ringing.length &&
    a.ringing.every((id, i) => id === b.ringing[i])
  );
}

// Входящие звонки на весь шелл: всплывашки справа сверху у каждого
// рекрутёра, пока звонящий слушает автоответчик, и окно звонка у того, кто
// нажал «Открыть звонок». Живёт в AppShell рядом с CandidateModalHost —
// внутри роутера (окну нужна ссылка на карточку) и только для вошедших.
//
// Софтфон окна принадлежит хосту, а не окну: прослушка стартует прямо из
// клика по всплывашке (жест пользователя нужен политике автовоспроизведения,
// а эффект при монтировании под StrictMode выполнился бы дважды).
//
// onActivity — только у программы для Windows (tray/TrayApp): окно программы
// живёт, пока есть что показать, и звонит, пока звонок ждёт ответа.
export function InboundCallHost({ onActivity }: { onActivity?: (a: InboundActivity) => void } = {}) {
  const { state: auth } = useAuth();
  const me = auth.status === "authed" ? { id: auth.user.id, full_name: auth.user.full_name } : null;
  const meRef = useRef<string | null>(null);
  meRef.current = me?.id ?? null;

  const [calls, dispatch] = useReducer(reduceInboundCalls, initialInboundCalls);
  const [session, setSession] = useState<InboundSession | null>(null);
  const [minimized, setMinimized] = useState(false);
  // id звонка в окне — синхронно, в обход перерисовки: ответ /listen, пришедший
  // после закрытия окна, должен это увидеть сразу.
  const sessionIdRef = useRef<string | null>(null);
  // Последнее отрисованное состояние окна — для колбэков, живущих дольше рендера.
  const sessionRef = useRef<InboundSession | null>(null);
  sessionRef.current = session;
  const listenSeq = useRef(0);
  const phone = useSoftphone();
  // Вкладка уже на звонке (любом, в том числе исходящем из карточки).
  const busy = useSyncExternalStore(subscribeActiveCall, hasActiveCall);

  // ---- снимок ----
  const loadSeq = useRef(0);
  // chat-service только что перезапустился и ещё не сверился с АТС: список
  // может быть неполным, а всплывашки звонков, кончившихся за перезапуск, по
  // нему не закрыть. Спрашиваем ещё раз, пока не сверится (или не надоест).
  const resyncRetry = useRef<{ timer: number | undefined; left: number }>({ timer: undefined, left: 0 });
  const load = useCallback((fromRetry = false) => {
    window.clearTimeout(resyncRetry.current.timer);
    if (!fromRetry) resyncRetry.current.left = RESYNC_RETRIES;
    const seq = ++loadSeq.current;
    const requestedAt = Date.now();
    api.inboundCalls().then(
      (r) => {
        // Ответ на более ранний запрос пришёл позже — он старее того, что уже есть.
        if (seq !== loadSeq.current) return;
        const list = Array.isArray(r.calls) ? r.calls : [];
        // Бекенд, ещё не сверившийся с АТС (или старый, без этого поля), — не
        // повод что-то закрывать по отсутствию в списке.
        const resynced = r.resynced === true;
        dispatch({ type: "snapshot", calls: list, resynced, me: meRef.current, now: Date.now(), requestedAt });
        setSession((s) => (s ? sessionOnSnapshot(s, { calls: list, resynced, requestedAt }) : s));
        if (!resynced && r.enabled && resyncRetry.current.left > 0) {
          resyncRetry.current.left -= 1;
          resyncRetry.current.timer = window.setTimeout(() => load(true), RESYNC_RETRY_MS);
        }
      },
      // Нет маршрута (старый chat-service) или сети — звонков пока просто нет;
      // следующее переподключение потока спросит снова.
      () => {},
    );
  }, []);

  useEffect(() => {
    load();
    const retry = resyncRetry.current;
    return () => window.clearTimeout(retry.timer);
  }, [load]);

  // ---- поток ----
  const onEvent = (ev: RealtimeEvent) => {
    if (ev.kind !== "inbound_call") return;
    const call = ev.call as InboundCall | undefined;
    if (!call || typeof call.id !== "string") return;
    const now = Date.now();
    dispatch({ type: "event", call, me: meRef.current, now });
    setSession((s) => (s ? sessionOnEvent(s, call, now) : s));
  };
  // На каждом (пере)подключении — снимок заново: за разрыв события потеряны.
  // Подписка живёт всегда, даже когда бекенд ответил `enabled: false`:
  // включают приём звонков перезапуском chat-service, поток при этом
  // переподключается, и снимок сам покажет, что звонки пошли. Иначе вкладка,
  // открытая до включения, так и осталась бы глухой до перезагрузки.
  useRealtime(onEvent, load);

  // ---- таймеры: смена фаз всплывашек и часы на них ----
  useEffect(() => {
    const next = nextExpiry(calls);
    if (next === null) return;
    const t = window.setTimeout(
      () => dispatch({ type: "tick", now: Date.now() }),
      Math.max(0, next - Date.now()) + 16,
    );
    return () => window.clearTimeout(t);
  }, [calls]);

  const hasToasts = calls.items.length > 0;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!hasToasts) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [hasToasts]);

  // ---- окно звонка ----

  // Токен прослушки → исходящий вызов на него. Вызывается только из клика
  // (открыть звонок, «Слушать снова»): жест пользователя нужен политике
  // автовоспроизведения, а эффект при монтировании под StrictMode выполнился
  // бы дважды. Устройства — запомненные: список в окне появится позже, а
  // линия поднимается уже сейчас; окно поправит микрофон, если он пропал.
  const startListen = (call: InboundCall, micId: string, speakerId: string) => {
    const seq = ++listenSeq.current;
    const current = () => sessionIdRef.current === call.id && seq === listenSeq.current;
    api.inboundListen(call.id).then(
      ({ target, token }) => {
        if (!current()) return; // окно успели закрыть (или открыть заново)
        setSession((s) => (s && s.call.id === call.id ? { ...s, token } : s));
        // Пока ждали токен, звонок взяли или он кончился: окно уже показывает
        // итог, а дозвон на отозванный токен АТС всё равно отклонит.
        if ((sessionRef.current?.call.state ?? call.state) !== "ringing") return;
        return phone.listen({ target, micId, speakerId });
      },
      (e) => {
        if (!current()) return;
        const err = inboundCallError(e) ?? { status: 0, code: null, taken_by: null };
        setSession((s) => (s && s.call.id === call.id ? { ...s, listenError: err } : s));
      },
    );
  };

  const openCall = (item: ToastItem) => {
    const call = item.call;
    if (sessionIdRef.current || hasActiveCall() || !toastCanOpen(item) || !call.intercept_ready) return;
    sessionIdRef.current = call.id;
    const { micId, speakerId } = savedAudioDevices();
    setMinimized(false);
    setSession({
      call,
      callAt: item.infoAt,
      token: null,
      listenError: null,
      micId,
      speakerId,
      attempt: 0,
    });
    phone.reset();
    startListen(call, micId, speakerId);
  };

  // Прослушка оборвалась или не поднялась, а звонящий всё ещё в меню — ещё раз,
  // не закрывая окна. Устройства — те, что выбраны в окне (оно их запомнило).
  const relisten = async () => {
    const s = sessionRef.current;
    if (!s || s.call.state !== "ringing" || !s.call.intercept_ready) return;
    const callId = s.call.id;
    await phone.hangup(); // остатки прежней линии, если они есть
    if (sessionIdRef.current !== callId) return;
    // Пока окно висело, вкладка начала другой звонок (исходящий из карточки):
    // второй сеанс 101 из той же вкладки сорвал бы первый.
    if (hasActiveCall()) return;
    phone.reset();
    const { micId, speakerId } = savedAudioDevices();
    setSession((cur) =>
      cur && cur.call.id === callId
        ? { ...cur, token: null, listenError: null, micId, speakerId, attempt: cur.attempt + 1 }
        : cur,
    );
    startListen(s.call, micId, speakerId);
  };

  const closeSession = useCallback(
    async (final: boolean) => {
      const closing = sessionRef.current;
      sessionIdRef.current = null;
      setSession(null);
      setMinimized(false);
      // Итог звонка окно уже показало — всплывашка того же звонка («взял
      // такой-то») не должна выскочить следом. Но и не хороним: если перехват
      // откатится (Bridge на АТС не состоялся), событие `ringing` вернёт её.
      // Звонящий ещё в меню — пусть вернётся сразу: его можно открыть снова.
      if (closing && (final || closing.call.state !== "ringing")) {
        dispatch({ type: "drop", id: closing.call.id, now: Date.now() });
      }
      // Только своя линия: из прослушки звонящий остаётся в меню, из разговора
      // АТС кладёт и его (Bridge с флагом x).
      await phone.hangup();
      phone.reset();
    },
    [phone],
  );

  // Всплывашку звонка, открытого в окне, не показываем — окно и есть он.
  const toasts = calls.items.filter((i) => i.call.id !== session?.call.id);

  const activity: InboundActivity = {
    ringing: toasts.filter(toastCanOpen).map((i) => i.call.id),
    toasts: toasts.length,
    session: session !== null,
  };
  const reported = useRef<InboundActivity | null>(null);
  const onActivityRef = useRef(onActivity);
  onActivityRef.current = onActivity;
  useEffect(() => {
    if (reported.current && sameActivity(reported.current, activity)) return;
    reported.current = activity;
    onActivityRef.current?.(activity);
  });

  // Контейнер в DOM всегда, даже пустой: живую область, вставленную вместе с
  // содержимым, дикторы часто не объявляют.
  return (
    <div className="icall-stack" role="region" aria-label="Звонки">
      {session && (
        <InboundCallModal
          key={session.call.id}
          session={session}
          me={me}
          phone={phone}
          minimized={minimized}
          busy={busy}
          onMinimize={setMinimized}
          onRetry={() => void relisten()}
          onClose={(final) => void closeSession(final)}
        />
      )}
      {/* Объявляются только всплывашки: часы в них скрыты от чтения, иначе
          экранный диктор зачитывал бы каждую секунду. */}
      <div className="icall-toasts" aria-live="polite">
        {toasts.map((item) => (
          <InboundCallToast
            key={item.call.id}
            item={item}
            now={now}
            blockedReason={openBlockedReason(item.call, !!session || busy)}
            onOpen={() => openCall(item)}
            onDismiss={() => dispatch({ type: "dismiss", id: item.call.id, now: Date.now() })}
          />
        ))}
      </div>
      {/* Сюда порталом встаёт свёрнутая плашка исходящего звонка (CallModal):
          стопка одна, и плашки с всплывашками в ней не наезжают друг на друга.
          В разметке — последним (вёрстка окна программы считает первых детей
          стопки), на экране — первым: order в inboundCall.css. */}
      <div className="icall-slot" ref={setCallStackSlot} />
    </div>
  );
}

export function InboundCallToast({
  item,
  now,
  blockedReason,
  onOpen,
  onDismiss,
}: {
  item: ToastItem;
  now: number;
  blockedReason: OpenBlock | null;
  onOpen: () => void;
  onDismiss: () => void;
}) {
  const { call, phase } = item;
  const title = callerTitle(call);
  const number = callerNumber(call);
  const ivr = ivrLabel(call.ivr_step);
  const listeners = listenersLabel(call.listeners);
  // И пока уезжает — чтобы текст не мигнул обратно на «звонит».
  const taken = phase !== "live" && call.state === "taken";
  // Взятый до перезапуска chat-service звонок приходит без имени (taken_by: null).
  const takenBy = taken ? call.taken_by : null;
  const live = phase === "live";

  // Вторая строка: номер под именем; для незнакомого номера (он уже в
  // заголовке) — что его нет в базе.
  let numberLine: string | null = null;
  if (call.person?.name) numberLine = number;
  else if (!call.person && number) numberLine = "Нет в базе";

  return (
    <section
      className={
        "icall-toast" +
        (phase === "taken" ? " icall-toast--taken" : "") +
        (phase === "leaving" ? " icall-toast--leaving" : "")
      }
      aria-label={`Входящий звонок: ${title}`}
    >
      <div className="icall-toast-main">
        <CallerAvatar call={call} size={44} ringing={live} />
        <div className="icall-toast-text">
          <div className="icall-toast-title" title={title}>
            {title}
          </div>
          {numberLine && (
            <div className={"icall-toast-num" + (call.person ? "" : " icall-toast-num--muted")}>{numberLine}</div>
          )}
          {taken ? (
            <div className="icall-toast-sub icall-toast-sub--taken">
              {takenBy ? (
                <>
                  <Avatar name={takenBy.name} src={userAvatarUrl(takenBy.user_id)} size={18} />
                  <span>
                    Взял(а) звонок: <b>{takenBy.name}</b>
                  </span>
                </>
              ) : (
                <span>Звонок уже принят</span>
              )}
            </div>
          ) : (
            <div className="icall-toast-sub">
              <span className="icall-live-dot" aria-hidden="true" />
              <span>
                Входящий звонок · автоответчик ·{" "}
                <span className="icall-timer" aria-hidden="true">
                  {fmtClock(elapsedSec(call.started_at, now))}
                </span>
              </span>
            </div>
          )}
          {!taken && (ivr || listeners) && (
            <div className="icall-toast-tags">
              {ivr && <span className="icall-chip">выбрал: {ivr}</span>}
              {listeners && (
                <span className="icall-chip icall-chip--listen">
                  <Icon name="headphones" size={12} strokeWidth={1.8} />
                  {listeners}
                </span>
              )}
            </div>
          )}
        </div>
        <button type="button" className="icall-icon-btn icall-toast-x" onClick={onDismiss} aria-label="Скрыть" title="Скрыть">
          <Icon name="close" size={14} />
        </button>
      </div>
      {!taken && (
        // title — на обёртке: у отключённой кнопки подсказка показывается не везде.
        <span className="icall-toast-actions" title={blockedReason?.title}>
          <button
            type="button"
            className="icall-open"
            onClick={onOpen}
            disabled={!toastCanOpen(item) || blockedReason !== null}
            aria-describedby={blockedReason ? `icall-why-${item.call.id}` : undefined}
          >
            <Icon name="phone" size={15} strokeWidth={1.8} />
            Открыть звонок
          </button>
          {blockedReason && (
            <span className="icall-toast-why" id={`icall-why-${item.call.id}`}>
              {blockedReason.hint}
            </span>
          )}
        </span>
      )}
    </section>
  );
}
